import React, { useMemo } from 'react';
import { Layers } from 'lucide-react';

interface ExamResultListProps {
    data: any[];
    metadata: any;
    examName?: string;
    examDate?: string;
    companyLogo?: string;
    companyName?: string;
}

export const ExamResultList: React.FC<ExamResultListProps> = ({ data, metadata, examName, examDate, companyLogo, companyName }) => {
    const subjectColumns = useMemo(() => {
        if (!data || data.length === 0) return [];

        const testNames = metadata?.testNames || {};

        let testIds = Object.keys(testNames);
        if (testIds.length === 0 && data[0]?.answers) testIds = Object.keys(data[0].answers);

        testIds.sort((a, b) => {
            const numA = parseInt(a);
            const numB = parseInt(b);

            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
        });

        return testIds.map(id => ({
            id,
            name: testNames[id] || `Test ${id}`
        }));
    }, [data, metadata]);

    const processedData = useMemo(() => {
        let rows = data.map((student) => {
            const stats = student.stats || {};
            const answers = student.answers || {};

            let totalCorrect = 0;
            let totalIncorrect = 0;
            let totalNet = 0;

            Object.values(answers).forEach((a: any) => {
                totalCorrect += (a.correct || 0);
                totalIncorrect += (a.incorrect || 0);

                const n = a.net !== undefined ? a.net : ((a.correct || 0) - (a.incorrect || 0) * 0.25);
                totalNet += n;
            });

            let score = stats.totalScore;
            if (!score || score === 0) {
                score = 100 + (totalNet * 3.4);

                if (score > 500) score = 500;
                if (score < 100) score = 100;

                if (totalNet <= 0) score = 0;
            };

            return {
                ...student,
                computed: {
                    totalCorrect,
                    totalIncorrect,
                    totalNet,
                    finalScore: score
                },
                getSubjectStats: (testId: string) => {
                    const ans = answers[testId];
                    if (!ans) return { correct: 0, incorrect: 0, net: 0 };
                    return {
                        correct: ans.correct || 0,
                        incorrect: ans.incorrect || 0,
                        net: ans.net || (ans.correct - (ans.incorrect * 0.25))
                    };
                }
            };
        });

        rows.sort((a, b) => b.computed.finalScore - a.computed.finalScore);

        rows.forEach((r, i) => {
            r.computed.generalRank = i + 1;
        });

        const branchGroups: Record<string, any[]> = {};
        rows.forEach(r => {
            const b = r.student?.department?.name || 'Unassigned';
            if (!branchGroups[b]) branchGroups[b] = [];
            branchGroups[b].push(r);
        });

        Object.values(branchGroups).forEach(group => {
            group.sort((a, b) => b.computed.finalScore - a.computed.finalScore);
            group.forEach((r, i) => {
                r.computed.branchRank = i + 1;
            });
        });

        return rows;
    }, [data]);

    const generalAverages = useMemo(() => {
        if (!processedData.length) return null;

        const totals: Record<string, { correct: number, incorrect: number, net: number }> = {};
        let totalCorrect = 0, totalIncorrect = 0, totalNet = 0, totalScore = 0;

        processedData.forEach(row => {
            subjectColumns.forEach(sub => {
                const stats = row.getSubjectStats(sub.id);
                if (!totals[sub.id]) totals[sub.id] = { correct: 0, incorrect: 0, net: 0 };
                totals[sub.id].correct += stats.correct;
                totals[sub.id].incorrect += stats.incorrect;
                totals[sub.id].net += stats.net;
            });

            totalCorrect += row.computed.totalCorrect;
            totalIncorrect += row.computed.totalIncorrect;
            totalNet += row.computed.totalNet;
            totalScore += row.computed.finalScore;
        });

        const count = processedData.length;
        const result: any = {
            subjects: {},
            totalCorrect: totalCorrect / count,
            totalIncorrect: totalIncorrect / count,
            totalNet: totalNet / count,
            totalScore: totalScore / count
        };

        subjectColumns.forEach(sub => {
            result.subjects[sub.id] = {
                correct: totals[sub.id].correct / count,
                incorrect: totals[sub.id].incorrect / count,
                net: totals[sub.id].net / count
            };
        });

        return result;
    }, [processedData, subjectColumns]);

    const statsRows = useMemo(() => {
        if (!generalAverages) return [];

        const generateStats = (multiplier: number, studentCount: number, label: string) => {
            const mockSub: any = {};
            subjectColumns.forEach(sub => {
                mockSub[sub.id] = {
                    correct: generalAverages.subjects[sub.id].correct * multiplier,
                    incorrect: generalAverages.subjects[sub.id].incorrect * multiplier,
                    net: generalAverages.subjects[sub.id].net * multiplier
                };
            });

            return {
                label,
                studentCount,
                subjects: mockSub,
                totalCorrect: generalAverages.totalCorrect * multiplier,
                totalIncorrect: generalAverages.totalIncorrect * multiplier,
                totalNet: generalAverages.totalNet * multiplier,
                totalScore: generalAverages.totalScore * multiplier
            };
        };

        return [] as any[];
    }, [generalAverages, subjectColumns]);

    const formatDate = (d: string) => {
        try {
            return new Date(d).toLocaleDateString('tr-TR');
        } catch {
            return d;
        }
    };

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Layers className="w-16 h-16 mb-4 opacity-20" />
                <p>Listelenecek veri bulunamadı.</p>
            </div>
        );
    }

    const renderHeader = (page: number, totalPages: number) => (
        <div className="mb-2 relative">
            <div className="absolute top-0 right-0 text-[10px] text-gray-600">
                Sayfa {page} / {totalPages}
            </div>

            <div className="flex items-end mb-4">
                <div className="flex flex-row items-center w-64 shrink-0 mr-4 gap-3">
                    {companyLogo ? (
                        <img src={companyLogo} alt="Logo" className="w-16 h-16 object-contain rounded-full border border-gray-200" />
                    ) : (
                        <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center shrink-0">
                            <span className="font-bold text-lg">DB</span>
                        </div>
                    )}
                    <div className="font-bold text-lg tracking-tight whitespace-nowrap uppercase">{companyName || 'SAYE EĞİTİM'}</div>
                </div>

                <div className="flex-1 text-center pb-2">
                    <h1 className="text-2xl font-bold text-black uppercase tracking-tight scale-y-110">{examName || 'TÜRKİYE GENELİ TYT İLK PROVA (T1225)'}</h1>
                </div>

                <div className="w-32"></div>
            </div>

            <div className="text-[11px] font-bold text-black mb-1">
                Sınav Tarihi : {formatDate(examDate || new Date().toISOString())}
            </div>
        </div>
    );

    const renderFooter = () => (
        <div className="mt-auto p-2 border-t border-gray-200 bg-gray-50 text-[10px] text-gray-500 flex justify-between items-center">
            <div>
                {companyName || 'Saye Eğitim'}
            </div>
            <div>
                {new Date().toLocaleString('tr-TR')}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-8 items-center bg-gray-100 pb-8">

            <div className="bg-white shadow-lg flex flex-col overflow-hidden" style={{ width: '297mm', minHeight: '210mm', padding: '10mm', fontFamily: '"Arial Narrow", "Roboto Condensed", sans-serif' }}>
                {renderHeader(1, 2)}

                <div className="flex-1">
                    <table className="w-full text-center border-collapse text-[9px] min-w-max border border-black leading-none">
                        <thead>
                            <tr className="bg-[#D9E1F2]">
                                <th rowSpan={2} className="p-0.5 border border-black font-bold whitespace-nowrap text-black w-8">SIRA<br />NO</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold whitespace-nowrap text-black w-8">ŞB</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold text-left min-w-[120px] text-black px-1">ADI SOYADI</th>

                                {subjectColumns.map(sub => (
                                    <th key={sub.id} colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black uppercase tracking-tight">
                                        {sub.name.toLocaleUpperCase('tr-TR')}
                                    </th>
                                ))}

                                <th colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black">TOPLAM</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">SINAV<br />PUANI</th>

                                <th rowSpan={2} className="p-0.5 border border-black font-bold w-6 text-[8px] text-black">ŞB<br />DRC.</th>
                                <th rowSpan={2} className="p-0.5 border border-black font-bold w-6 text-[8px] text-black">GENEL<br />DRC.</th>
                            </tr>
                            <tr className="bg-[#D9E1F2]">
                                {subjectColumns.map(sub => (
                                    <React.Fragment key={`${sub.id}-sub`}>
                                        <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                        <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                        <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                    </React.Fragment>
                                ))}
                                <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                <th className="border border-black py-0.5 font-normal w-6">N.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {processedData.map((row, idx) => {
                                const isEven = idx % 2 === 0;
                                const rowClass = isEven ? 'bg-[#DCE6F1]' : 'bg-white';
                                return (
                                    <tr key={row.id} className={rowClass}>
                                        <td className="border border-black py-0.5 font-bold text-black">{idx + 1}</td>
                                        <td className="border border-black py-0.5 text-black font-medium">{row.student?.department?.name || ''}</td>
                                        <td className="border border-black py-0.5 text-left px-1 font-bold text-black uppercase whitespace-nowrap overflow-hidden max-w-[150px] text-ellipsis">
                                            {(row.student?.firstName && row.student?.lastName)
                                                ? `${row.student.firstName} ${row.student.lastName}`
                                                : (row.results?.studentInfo?.fullName || 'İsimsiz')}
                                        </td>

                                        {subjectColumns.map(sub => {
                                            const stats = row.getSubjectStats(sub.id);
                                            return (
                                                <React.Fragment key={`${row.id}-${sub.id}`}>
                                                    <td className="border border-gray-400 border-l-black border-r-black py-0.5 text-black">{stats.correct}</td>
                                                    <td className="border border-gray-400 border-r-black py-0.5 text-black">{stats.incorrect}</td>
                                                    <td className="border border-black py-0.5 font-bold text-black">{Number(stats.net).toFixed(2)}</td>
                                                </React.Fragment>
                                            );
                                        })}

                                        <td className="border border-black py-0.5 font-medium text-black">{row.computed.totalCorrect}</td>
                                        <td className="border border-black py-0.5 font-medium text-black">{row.computed.totalIncorrect}</td>
                                        <td className="border border-black py-0.5 font-bold text-black">{Number(row.computed.totalNet).toFixed(2)}</td>

                                        <td className="border border-black py-0.5 font-bold text-black">{Number(row.computed.finalScore).toFixed(3)}</td>

                                        <td className="border border-black py-0.5 text-black">{row.computed.branchRank}</td>
                                        <td className="border border-black py-0.5 text-black">{row.computed.generalRank}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {renderFooter()}
            </div>

            {generalAverages && (
                <div className="bg-white shadow-lg flex flex-col overflow-hidden" style={{ width: '297mm', minHeight: '210mm', padding: '10mm', fontFamily: '"Arial Narrow", "Roboto Condensed", sans-serif' }}>
                    {renderHeader(2, 2)}

                    <div className="flex-1 flex flex-col justify-start pt-8">
                        <table className="w-full text-center border-collapse text-[9px] min-w-max border border-black leading-none">
                            <thead>
                                <tr className="bg-[#D9E1F2]">
                                    <th rowSpan={2} colSpan={3} className="p-0.5 border border-black font-bold text-left px-2 text-black w-64">İSTATİSTİK BİLGİLERİ</th>
                                    {subjectColumns.map(sub => (
                                        <th key={sub.id} colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black uppercase tracking-tight">
                                            {sub.name.toLocaleUpperCase('tr-TR')}
                                        </th>
                                    ))}
                                    <th colSpan={3} className="px-0.5 py-1 border border-black font-bold text-black">TOPLAM</th>
                                    <th rowSpan={2} className="p-0.5 border border-black font-bold text-black w-12">ORT.<br />PUAN</th>
                                </tr>
                                <tr className="bg-[#D9E1F2]">
                                    {subjectColumns.map(sub => (
                                        <React.Fragment key={`${sub.id}-sub-2`}>
                                            <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                            <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                            <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                        </React.Fragment>
                                    ))}
                                    <th className="border border-black py-0.5 font-normal w-6">D.</th>
                                    <th className="border border-black py-0.5 font-normal w-6">Y.</th>
                                    <th className="border border-black py-0.5 font-normal w-6">N.</th>
                                </tr>
                            </thead>
                            <tbody>

                                <tr className="bg-[#DCE6F1]">
                                    <td colSpan={3} className="border border-black py-2 text-left px-2 text-black font-bold">GENEL ALT ORTALAMALAR</td>
                                    {subjectColumns.map(sub => (
                                        <React.Fragment key={`avg-${sub.id}`}>
                                            <td className="border border-black py-2 text-black">{generalAverages.subjects[sub.id].correct.toFixed(2)}</td>
                                            <td className="border border-black py-2 text-black">{generalAverages.subjects[sub.id].incorrect.toFixed(2)}</td>
                                            <td className="border border-black py-2 text-black font-bold">{generalAverages.subjects[sub.id].net.toFixed(2)}</td>
                                        </React.Fragment>
                                    ))}
                                    <td className="border border-black py-2 text-black">{generalAverages.totalCorrect.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black">{generalAverages.totalIncorrect.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black font-bold">{generalAverages.totalNet.toFixed(2)}</td>
                                    <td className="border border-black py-2 text-black font-bold">{generalAverages.totalScore.toFixed(3)}</td>
                                </tr>

                                {statsRows.map((stat, idx) => (
                                    <tr key={idx} className="bg-[#FCD5B5]">
                                        <td colSpan={3} className="border border-black py-2 text-left px-2 text-black text-[9px] font-bold">
                                            {stat.label} ( {stat.studentCount} Öğrenci )
                                        </td>
                                        {subjectColumns.map(sub => (
                                            <React.Fragment key={`stat-${idx}-${sub.id}`}>
                                                <td className="border border-black py-2 text-black">{stat.subjects[sub.id].correct.toFixed(2)}</td>
                                                <td className="border border-black py-2 text-black">{stat.subjects[sub.id].incorrect.toFixed(2)}</td>
                                                <td className="border border-black py-2 text-black font-bold">{stat.subjects[sub.id].net.toFixed(2)}</td>
                                            </React.Fragment>
                                        ))}
                                        <td className="border border-black py-2 text-black">{stat.totalCorrect.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black">{stat.totalIncorrect.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black font-bold">{stat.totalNet.toFixed(2)}</td>
                                        <td className="border border-black py-2 text-black font-bold">{stat.totalScore.toFixed(3)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {renderFooter()}
                </div>
            )}
        </div>
    );
};